// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/ai-safety.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * The chatbot's deterministic safety layer (docs/AI_CHATBOT_SAFETY.md), the
 * same in every app and in AYNI XR's copy. It works without a model and
 * without a network, and it runs in front of the model and behind it:
 *
 *   - a visitor in distress gets a fixed, caring reply at once, in their
 *     language, with no model involved and no change to the sphere;
 *   - personal details (emails, phone numbers) are blanked before the words
 *     reach a model or the conversation's memory;
 *   - text the visitor did not write as a visitor (catalog entries, publisher
 *     notes, tool results) and the visitor's own words are fenced, so a fake
 *     "System:" line or a chat-template token stays plain text;
 *   - a reply is checked before it is shown or spoken: no swearing, and no
 *     pieces of the instructions or tool list.
 *
 * The model's own rules (ai-prompt.ts) and the gateway's moderation do the
 * rest; this layer is the floor that holds when they do not.
 */
import type { AiChatLanguage } from './ai-languages';

/** First-person statements of wanting to die or to be hurt, in the four chat languages. */
const DISTRESS: ReadonlyArray<RegExp> = [
  /\b(kill|hurt|harm|cut|end) myself\b/i,
  /\bsuicid(e|al)\b/i,
  /\bi (really )?(want|wanna|am going|'m going) to die\b/i,
  /\bi (don'?t|do not) want to (live|be alive|exist)( anymore)?\b/i,
  /\bend (it all|my life)\b/i,
  /\bself[- ]harm/i,
  /\b(matarme|suicidarme|suicidio|quitarme la vida)\b/i,
  /\b(quiero|me quiero|voy a) (morir|morirme|hacerme daño|hacer daño a mí)/i,
  /\bno quiero (vivir|seguir viviendo)\b/i,
  /\b(zelfmoord|suïcide|suicide)\b/i,
  /\b(mezelf|me) (van het leven beroven|iets aandoen|pijn doen|doden)\b/i,
  /\bik wil (dood|niet meer leven|er niet meer zijn)\b/i,
  /\b(suïcidi|suicidi|matar-me|treure'm la vida)\b/i,
  /\b(vull|em vull) (morir|fer mal|fer-me mal)\b/i,
  /\bvull fer-me mal\b/i,
  /\bno vull (viure|seguir vivint)\b/i
];

/** Whether the visitor says they may hurt themselves: then the fixed reply answers. */
export function isDistress(text: string): boolean {
  return DISTRESS.some((pattern) => pattern.test(text));
}

/**
 * The reply to someone in distress: warm, short, and pointing to people who
 * can help now. 112 reaches emergency services anywhere in Europe.
 */
export const DISTRESS_REPLY: Record<AiChatLanguage, string> = {
  en: 'I\'m really sorry you\'re feeling this way, and I\'m glad you said it. Please talk right now to someone you trust: a family member, a friend, a teacher or a member of staff here. If you might be in danger, call 112 (or your local emergency number). You don\'t have to go through this alone.',
  es: 'Siento mucho que te sientas así, y me alegra que lo hayas dicho. Por favor, habla ahora mismo con alguien de confianza: tu familia, un amigo, un profesor o alguien del personal de aquí. Si puedes estar en peligro, llama al 112. No tienes que pasar por esto solo.',
  nl: 'Wat naar dat je je zo voelt, en goed dat je het zegt. Praat alsjeblieft nu met iemand die je vertrouwt: iemand uit je familie, een vriend, een leraar of een medewerker hier. Als je in gevaar bent, bel 112. Je hoeft dit niet alleen te doen.',
  ca: 'Em sap molt de greu que et sentis així, i m\'alegra que ho hagis dit. Si us plau, parla ara mateix amb algú de confiança: la teva família, un amic, un professor o algú del personal d\'aquí. Si pots estar en perill, truca al 112. No has de passar per això sol.'
};

/** What stands in for a reply that cannot be shown, or a request the model must not answer. */
export const DECLINE_REPLY: Record<AiChatLanguage, string> = {
  en: 'I can\'t help with that here. Ask me about the sphere and the data on it: places, oceans, the weather, the climate or space.',
  es: 'Con eso no puedo ayudarte aquí. Pregúntame por la esfera y sus datos: lugares, océanos, el tiempo, el clima o el espacio.',
  nl: 'Daar kan ik je hier niet mee helpen. Vraag me iets over de bol en de gegevens erop: plaatsen, oceanen, het weer, het klimaat of de ruimte.',
  ca: 'Amb això no et puc ajudar aquí. Pregunta\'m per l\'esfera i les seves dades: llocs, oceans, el temps, el clima o l\'espai.'
};

const EMAIL = /[\p{L}\p{N}._%+-]+@[\p{L}\p{N}.-]+\.[\p{L}]{2,}/gu;
/**
 * A phone number: an international "+" and 8-15 digits, or 9-15 digits in a
 * run. Only spaces, dashes and brackets may sit between the digits, so
 * coordinates ("41.38, 2.17"), dates ("2026-09-20") and years stay.
 */
const PHONE = /(?<![\p{L}\p{N}.,])(\+\s?\d[\d\s()-]{6,18}\d|\d[\d\s()-]{7,18}\d)(?![\p{L}\p{N}]|[.,]\d)/gu;

function digitCount(text: string): number {
  return text.replace(/\D/g, '').length;
}

/** Years ("1990 2000 2010") and ISO dates are numbers a visitor asks about, not phone numbers. */
function looksLikeDates(match: string): boolean {
  const groups = match.split(/\D+/).filter(Boolean);
  return /\d{4}-\d{2}-\d{2}/.test(match) || groups.every((group) => /^[12]\d{3}$/.test(group));
}

/** Emails and phone numbers blanked out, before the words leave the device. */
export function redactPersonalData(text: string): string {
  return text
    .replace(EMAIL, '[email]')
    .replace(PHONE, (match) => {
      if (looksLikeDates(match)) return match;
      const digits = digitCount(match);
      const international = match.trimStart().startsWith('+');
      return (international ? digits >= 8 : digits >= 9) && digits <= 15 ? '[phone number]' : match;
    });
}

/**
 * Markers a model might read as structure: the fences this engine uses, and
 * the chat-template tokens of common open models. Removed from any text that
 * is not the instructions themselves.
 */
const STRUCTURE_MARKERS: ReadonlyArray<RegExp> = [
  /<\/?\s*(context|visitor_message|system|instructions?|untrusted|tool|assistant|user)\b[^>]*>/gi,
  /<\|[^|>]{1,40}\|>/g,
  /<\/?\s*(start|end)_of_turn\s*>/gi,
  /\[\/?INST\]/gi,
  /<<\/?SYS>>/gi
];

/** Untrusted text made safe to place inside a fence: no fences or template tokens of its own. */
export function fenceText(text: string): string {
  let clean = text;
  for (const marker of STRUCTURE_MARKERS) clean = clean.replace(marker, ' ');
  return clean.replace(/[ \t]{2,}/g, ' ').trim();
}

/** Words a reply on a public sphere must never contain, in the four chat languages. */
const PROFANITY = /\b(fuck\w*|shit\w*|bitch\w*|asshole|cunt|motherfuck\w*|mierda|joder|puta|put[oa]s|gilipollas|cabr[oó]n|coño|kut|klootzak|godverdomme|tering|merda|collons|fill de puta)\b/i;

/**
 * Phrases only the instructions contain. A reply quoting them is a leak,
 * whatever the visitor said to get it.
 */
const INSTRUCTION_MARKERS: ReadonlyArray<RegExp> = [
  /keep answers short/i,
  /two to four sentences/i,
  /never invent a dataset/i,
  /these rules always apply/i,
  /only this message gives you instructions/i,
  /<\/?\s*(context|visitor_message)\s*>/i
];

/** A tool's function name, as the model sees it (dataset_load, luma_probe…). */
const TOOL_NAME = /\b(catalog_search|dataset_load|view_(goto|zoom|rotation)|media_(play|pause|stop|seek|seekDate|speed)|sphere_slice|state_describe|luma_[a-zA-Z]+|pins_[a-z]+|khipu_[a-z]+|features_query|satellites_[a-z]+|lesson_[a-z]+|stats_[a-z]+|baseline_[a-z]+)\b/g;

export type ReplyVerdict = { ok: true } | { ok: false; reason: 'profanity' | 'instructions' };

/** Whether a reply may be shown and spoken. */
export function screenReply(text: string): ReplyVerdict {
  if (PROFANITY.test(text)) return { ok: false, reason: 'profanity' };
  if (INSTRUCTION_MARKERS.some((marker) => marker.test(text))) return { ok: false, reason: 'instructions' };
  // A tool name or two in passing is clumsy wording ("I set media_speed and
  // media_play"); a list of them is the tool list.
  const tools = new Set(text.match(TOOL_NAME) ?? []);
  if (tools.size >= 3) return { ok: false, reason: 'instructions' };
  return { ok: true };
}
